/**
 * CountryFlag — renders a country flag PNG via flagcdn.com.
 *
 * Usage:
 *   <CountryFlag iso="br" />
 *   <CountryFlag iso="GB" size="lg" alt="England" rounded />
 *
 * `iso` should be the ISO 3166-1 alpha-2 country code (e.g. "br", "us", "gb-eng").
 * flagcdn.com supports a few subnational codes too (gb-eng, gb-sct, gb-wls).
 */

import { CSSProperties } from 'react';

export type CountryFlagSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_PX: Record<CountryFlagSize, number> = {
  xs: 16,
  sm: 20,
  md: 28,
  lg: 40,
  xl: 60,
};

// flagcdn supports specific widths: 20, 40, 80, 160, 320, 640, etc.
const SIZE_CDN: Record<CountryFlagSize, number> = {
  xs: 20,
  sm: 20,
  md: 40,
  lg: 40,
  xl: 80,
};

export interface CountryFlagProps {
  iso: string;                 // ISO 3166-1 alpha-2 (case-insensitive)
  alt?: string;                // accessible label, e.g. country name
  size?: CountryFlagSize;
  rounded?: boolean;
  bordered?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function CountryFlag({
  iso,
  alt,
  size = 'md',
  rounded = false,
  bordered = true,
  className,
  style,
}: CountryFlagProps) {
  const code = (iso || '').trim().toLowerCase();
  if (!code) return null;

  const px = SIZE_PX[size];
  const cdnW = SIZE_CDN[size];
  const src = `https://flagcdn.com/w${cdnW}/${code}.png`;
  const src2x = `https://flagcdn.com/w${cdnW * 2}/${code}.png 2x`;

  // approx 4:3 aspect for most flags
  const height = Math.round(px * 0.75);

  return (
    <img
      src={src}
      srcSet={src2x}
      width={px}
      height={height}
      alt={alt ?? code.toUpperCase()}
      title={alt}
      loading="lazy"
      decoding="async"
      className={[
        'inline-block object-cover',
        rounded ? 'rounded' : '',
        bordered ? 'ring-1 ring-gray-200' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ ...style, width: px, height }}
    />
  );
}

export default CountryFlag;
